import { createStore } from "@xstate/store";

import type { ValidCellEmitInfo } from "~/runtime/cell-compiler/emit";
import type { CellID, CellInfo, CellInfoExecutable } from "~/state/types";

type CellsStoreContext = {
	cells: Record<string, CellInfo>;
	orderedList: CellID[];
	dirty: boolean;
	hash: string;
	cellInfosToRun: CellInfo[];
	lastSelectedCellID: CellID | undefined;
	selectedComputeCellIDs: CellID | CellID[] | undefined;
};

const initialState: CellsStoreContext = {
	cells: {},
	orderedList: [],
	cellInfosToRun: [],
	dirty: false,
	hash: "",
	lastSelectedCellID: undefined,
	selectedComputeCellIDs: undefined,
};

export const cellsStore = createStore({
	context: initialState,
	on: {
		reset: () => {
			return initialState;
		},

		setStatusExecutable: (context, event: { cellID: string; emitInfo: ValidCellEmitInfo }) => {
			const { cellID, emitInfo } = event;

			const cellFromStore = context.cells[cellID];

			if (cellFromStore === undefined) {
				throw new Error(`cell ${cellID} does not exist`);
			}

			const newCellInfo: CellInfoExecutable = {
				cell: cellFromStore.cell,
				analysis: cellFromStore.analysis,
				emitInfo,
				status: "executable",
			};

			return {
				...context,
				cells: {
					...context.cells,
					[cellID]: newCellInfo,
				},
			};
		},

		setStatusError: (context, event: { cellID: CellID; message: string }) => {
			const { cellID, message } = event;

			const cellFromStore = context.cells[cellID];
			if (!cellFromStore) {
				throw new Error(`cell ${cellID} does not exist`);
			}

			const newCellInfo: CellInfo = { ...cellFromStore, status: "error", message };

			return {
				...context,
				cells: {
					...context.cells,
					[cellID]: newCellInfo,
				},
			};
		},

		replaceAll: (context, event: { cells: CellInfo[]; setDirty?: boolean }) => {
			const { cells, setDirty } = event;

			return {
				cells: Object.fromEntries(cells.map((cellInfo) => [cellInfo.cell.id, cellInfo])),
				orderedList: cells.map((cellInfo) => cellInfo.cell.id),
				dirty: setDirty === false ? false : true,
				hash: "",
				cellInfosToRun: [],
				lastSelectedCellID: undefined,
				selectedComputeCellIDs: undefined,
			};
		},

		append: (context, event: { cellInfo: CellInfo }) => {
			const { cellInfo } = event;

			// eslint-disable-next-line no-prototype-builtins
			if (context.cells.hasOwnProperty(cellInfo.cell.id)) {
				throw Error(`Cell ${cellInfo.cell.id} already exists in store`);
			}

			return {
				...context,
				cells: {
					...context.cells,
					[cellInfo.cell.id]: cellInfo,
				},
				orderedList: [...context.orderedList, cellInfo.cell.id],
				dirty: true,
			};
		},

		setCell: (
			context,
			event: {
				cellInfo: CellInfo;
			}
		) => {
			const { cellInfo } = event;

			return {
				...context,
				cells: {
					...context.cells,
					[cellInfo.cell.id]: cellInfo,
				},
				dirty: true,
			};
		},

		setDirty: (context, event: boolean) => {
			return {
				...context,
				dirty: event,
			};
		},

		setCellInfosToRun: (context, event: { cellInfosToRun: CellInfo[] }) => {
			const { cellInfosToRun } = event;

			return {
				...context,
				cellInfosToRun,
				dirty: true,
			};
		},

		setOrderedList: (context, event: { orderedList: CellID[] }) => {
			const { orderedList } = event;

			return {
				...context,
				orderedList,
				dirty: true,
			};
		},

		setSelectedComputeCell: (context, event: { cellIDs: CellID[] }) => {
			const { cellIDs } = event;

			for (const cellID of cellIDs) {
				const cellFromStore = context.cells[cellID];
				if (!cellFromStore) {
					throw new Error(`cell ${cellID} does not exist`);
				}
				if (cellFromStore.cell.type === "ProseMirrorJSONCell") {
					throw new Error(`cell ${cellID} is not a compute cell`);
				}
			}

			return {
				...context,
				lastSelectedCellID: cellIDs[cellIDs.length - 1],
				selectedComputeCellIDs: cellIDs,
			};
		},

		unsetSelectedComputeCell: (context) => {
			return {
				...context,
				selectedComputeCellIDs: undefined,
			};
		},
	},
});
